import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ShoppingBag,
  ArrowLeft,
  Trash2,
  Plus,
  Minus,
  CreditCard,
  Truck,
  ShieldCheck,
  Sparkles
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useCart } from '../contexts/CartContext';
import { useLanguage } from '../contexts/LanguageContext';
import '../styles/cart-elegant.css';

const COPY = {
  es: {
    back: 'Volver',
    heroTitle: 'Tu selección cuidada',
    heroSubtitle: 'Confirma tu pedido, revisa los detalles y continúa disfrutando de la experiencia Relatos de Papel.',
    heroStats: [
      { key: 'items', label: 'Artículos' },
      { key: 'total', label: 'Subtotal' },
      { key: 'savings', label: 'Ahorros' }
    ],
    emptyTitle: 'Tu carrito está listo para llenarse',
    emptyMessage: 'Explora el catálogo y añade libros, cómics o mangas a tu biblioteca personal.',
    emptyCta: 'Volver al catálogo',
    quantity: 'Cantidad',
    remove: 'Eliminar',
    summaryTitle: 'Resumen de compra',
    subtotal: 'Subtotal',
    savings: 'Ahorros',
    shipping: 'Envío',
    shippingFree: 'Gratis',
    total: 'Total a pagar',
    shippingNote: 'Agrega {amount} en productos para envío gratis',
    checkout: 'Realizar compra',
    checkoutSecondary: 'Continuar explorando',
    disclaimer: 'Proyecto demostrativo: las transacciones no son reales.',
    savingsPill: 'Club Relatos ahorra',
    shippingPill: 'Envío boutique',
    infoToast: 'Este es un proyecto demostrativo; no se procesará el pago.'
  },
  en: {
    back: 'Back',
    heroTitle: 'Your curated selection',
    heroSubtitle: 'Review your order, check the details and keep enjoying the Relatos de Papel experience.',
    heroStats: [
      { key: 'items', label: 'Items' },
      { key: 'total', label: 'Subtotal' },
      { key: 'savings', label: 'Savings' }
    ],
    emptyTitle: 'Your cart is ready to be filled',
    emptyMessage: 'Browse the catalogue and add books, comics or manga to your personal library.',
    emptyCta: 'Back to catalogue',
    quantity: 'Quantity',
    remove: 'Remove',
    summaryTitle: 'Order summary',
    subtotal: 'Subtotal',
    savings: 'Savings',
    shipping: 'Shipping',
    shippingFree: 'Free',
    total: 'Total due',
    shippingNote: 'Add {amount} for complimentary shipping',
    checkout: 'Proceed to checkout',
    checkoutSecondary: 'Keep browsing',
    disclaimer: 'Showcase project: no real transactions are processed.',
    savingsPill: 'Relatos Club saves',
    shippingPill: 'Boutique shipping',
    infoToast: 'This is a showcase project; the payment will not be processed.'
  }
};

const CartPage = () => {
  const navigate = useNavigate();
  const {
    items,
    total,
    itemCount,
    removeItem,
    updateQuantity,
    clearCart,
    formatPrice,
    getTotalSavings,
    getShippingCost,
    getFinalTotal
  } = useCart();
  const { language } = useLanguage();
  const copy = COPY[language];

  const savings = getTotalSavings();
  const shippingCost = getShippingCost();
  const finalTotal = getFinalTotal();

  const handleCheckout = () => {
    // 1. Mostrar alerta (Toast en este caso, o window.alert dado el requisito de "Mostrar alerta")
    toast.success("¡Compra confirmada con éxito! Gracias por tu pedido.");
    // window.alert("¡Compra confirmada!"); // Descomentar si el profesor es muy estricto con "Alerta nativa"

    // 2. Vaciar carrito
    clearCart();

    // 3. Redirigir a página principal
    setTimeout(() => {
      navigate('/home');
    }, 1500);
  };

  const freeShippingNotice =
    shippingCost === 0
      ? null
      : copy.shippingNote.replace('{amount}', formatPrice(Math.max(0, 25 - total)));

  const heroStats = {
    items: itemCount,
    total: formatPrice(total),
    savings: savings > 0 ? formatPrice(savings) : formatPrice(0)
  };

  return (
    <div className="cart-page">
      <div className="cart-container">
        <button
          type="button"
          className="cart-back-button"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft size={18} />
          {copy.back}
        </button>

        <section className="cart-hero">
          <div className="cart-hero__content">
            <div className="cart-hero__pill">
              <ShieldCheck size={16} />
              {copy.savingsPill}
            </div>
            <h1 className="cart-hero__title">{copy.heroTitle}</h1>
            <p className="cart-hero__subtitle">{copy.heroSubtitle}</p>

            <div className="cart-hero__stats">
              {copy.heroStats.map((stat) => (
                <div key={stat.key} className="cart-stat">
                  <span className="cart-stat__label">{stat.label}</span>
                  <span className="cart-stat__value">{heroStats[stat.key]}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="cart-hero__banner">
            <Sparkles size={18} />
            <div>
              <strong>{copy.shippingPill}</strong>
              <span>{freeShippingNotice ?? (language === 'es' ? 'Envíos gratis desde $25 USD' : 'Complimentary shipping from $25 USD')}</span>
            </div>
          </div>
        </section>

        <div className="cart-layout">
          <motion.section
            className="cart-main"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {items.length === 0 ? (
              <div className="cart-empty">
                <ShoppingBag size={42} />
                <h2>{copy.emptyTitle}</h2>
                <p>{copy.emptyMessage}</p>
                <button
                  type="button"
                  className="cart-primary-button"
                  onClick={() => navigate('/')}
                >
                  {copy.emptyCta}
                </button>
              </div>
            ) : (
              <>
                <div className="cart-main__toolbar">
                  <button
                    type="button"
                    className="cart-link"
                    onClick={() => navigate('/')}
                  >
                    <ArrowLeft size={16} />
                    {copy.checkoutSecondary}
                  </button>
                  <button
                    type="button"
                    className="cart-link cart-link--danger"
                    onClick={clearCart}
                  >
                    <Trash2 size={15} />
                    {copy.clear}
                  </button>
                </div>

                <AnimatePresence>
                  {items.map((item) => (
                    <motion.article
                      key={item.id}
                      className="cart-item"
                      initial={{ opacity: 0, y: 18 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -18 }}
                      transition={{ duration: 0.25 }}
                    >
                      <div className="cart-item__media">
                        <img src={item.coverUrl} alt={item.title} loading="lazy" />
                      </div>

                      <div className="cart-item__content">
                        <div className="cart-item__header">
                          <h3>{item.title}</h3>
                          <button
                            type="button"
                            className="cart-link cart-link--danger"
                            onClick={() => removeItem(item.id)}
                          >
                            <Trash2 size={14} />
                            {copy.remove}
                          </button>
                        </div>
                        <p className="cart-item__author">{item.author}</p>
                        <p className="cart-item__meta">
                          {item.category} • {item.language}
                        </p>

                        <div className="cart-item__footer">
                          <div className="cart-quantity">
                            <span>{copy.quantity}</span>
                            <div className="cart-quantity__controls">
                              <button
                                type="button"
                                onClick={() => updateQuantity(item.id, item.quantity - 1)}
                                aria-label="Decrease quantity"
                              >
                                <Minus size={14} />
                              </button>
                              <span>{item.quantity}</span>
                              <button
                                type="button"
                                onClick={() => updateQuantity(item.id, item.quantity + 1)}
                                aria-label="Increase quantity"
                              >
                                <Plus size={14} />
                              </button>
                            </div>
                          </div>

                          <div className="cart-item__price">
                            <span className="cart-item__price-current">
                              {formatPrice(item.price * item.quantity)}
                            </span>
                            {item.originalPrice && (
                              <span className="cart-item__price-original">
                                {formatPrice(item.originalPrice * item.quantity)}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </motion.article>
                  ))}
                </AnimatePresence>
              </>
            )}
          </motion.section>

          <aside className="cart-summary">
            <div className="cart-summary__card">
              <h2>{copy.summaryTitle}</h2>

              <div className="cart-summary__row">
                <span>{copy.subtotal}</span>
                <span>{formatPrice(total)}</span>
              </div>

              {savings > 0 && (
                <div className="cart-summary__row cart-summary__row--positive">
                  <span>{copy.savings}</span>
                  <span>-{formatPrice(savings)}</span>
                </div>
              )}

              <div className="cart-summary__row">
                <span>{copy.shipping}</span>
                <span>
                  {shippingCost === 0 ? copy.shippingFree : formatPrice(shippingCost)}
                </span>
              </div>

              <div className="cart-summary__divider" />

              <div className="cart-summary__total">
                <span>{copy.total}</span>
                <span>{formatPrice(finalTotal)}</span>
              </div>

              {freeShippingNotice && (
                <div className="cart-summary__notice">
                  <Truck size={15} />
                  <span>{freeShippingNotice}</span>
                </div>
              )}

              <button
                type="button"
                className="cart-primary-button cart-primary-button--full"
                onClick={handleCheckout}
                disabled={items.length === 0}
              >
                <CreditCard size={18} />
                {copy.checkout}
              </button>

              <button
                type="button"
                className="cart-secondary-button"
                onClick={() => navigate('/')}
              >
                {copy.checkoutSecondary}
              </button>

              <p className="cart-summary__disclaimer">{copy.disclaimer}</p>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default CartPage;
